import React from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Carousel from 'react-bootstrap/Carousel';

const Projects: React.FC = () => {
  const parseMarkdownBold = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const projects = [
    {
      title: "AntGame.io",
      description: "A full-stack web game with advanced anti-cheat systems, procedural map generation, and custom infrastructure. Peak of 300 daily active users with sustained ~20 DAU for the past year.",
      technologies: ["MongoDB", "React", "Express", "TypeScript", "Node.js"],
      features: [
        "Advanced anti-cheat System using deterministic simulation **to prevent client-side manipulation**",
        "Backend verification for simulation replays **to ensure fair play and validate high scores**",
        "Custom leaderboard caching mechanism **for instant, responsive load times**",
        "Procedural map generation with customizable parameters",
        "Custom feature flagging system for controlled rollouts"
      ],
      screenshots: [
        "/assets/projects/antgame1.png",
        "/assets/projects/antgame2.png",
        "/assets/projects/antgame3.png",
        "/assets/projects/antgame4.png"
      ],
      liveUrl: "https://antgame.io",
      githubUrl: "https://github.com/Cuzzo01/antgame.io",
      status: "Feature Complete"
    }
  ];

  return (
    <section id="projects" className="py-5">
      <Container>
        {/* <h2 className="text-center mb-5">Featured Projects</h2> */}
        <h2 className="text-center mb-5">Featured Project</h2>
        <Row className="justify-content-center">
          {projects.map((project, index) => (
            <Col lg={10} key={index} className="mb-4">
              <Card className="bg-dark border-secondary h-100 text-light">
                <Card.Body>
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3">
                    <div className="mb-2 mb-md-0">
                      <Card.Title className="h3 mb-1">{project.title}</Card.Title>
                      <span className="badge bg-success">{project.status}</span>
                    </div>
                    <div className="d-flex gap-2 mt-1 mt-md-0">
                      {project.githubUrl && (
                        <Button 
                          variant="outline-light" 
                          href={project.githubUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <i className="bi bi-github me-1"></i> Code
                        </Button>
                      )}
                      <Button 
                        variant="primary" 
                        href={project.liveUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        View Live Site
                      </Button>
                    </div>
                  </div>
                  
                  <Card.Text className="mb-3 text-light">
                    {project.description}
                  </Card.Text>
                  
                  <Row className="mb-3">
                    <Col md={7}>
                      <div className="mb-3">
                        <h5 className="text-light mb-2">Key Features:</h5>
                        <ul className="mb-0 feature-list">
                          {project.features.map((feature, idx) => (
                            <li key={idx} className="mb-2">{parseMarkdownBold(feature)}</li>
                          ))}
                        </ul>
                      </div>
                    </Col>
                    
                    <Col md={5}>
                      <div>
                        <h6 className="text-light mb-2">Technologies:</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {project.technologies.map((tech, idx) => (
                            <span key={idx} className="badge bg-secondary">{tech}</span>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  
                  {project.screenshots && project.screenshots.length > 0 && (
                    <div>
                      <h5 className="mb-2 text-light">Screenshots:</h5>
                      <Carousel 
                        className="project-screenshots enhanced-controls" 
                        indicators={project.screenshots.length > 1}
                        prevIcon={<span aria-hidden="true" className="carousel-control-prev-icon enhanced-control-icon" />}
                        nextIcon={<span aria-hidden="true" className="carousel-control-next-icon enhanced-control-icon" />}
                      >
                        {project.screenshots.map((screenshot, idx) => (
                          <Carousel.Item key={idx}>
                            <img
                              className="d-block w-100 rounded"
                              src={screenshot}
                              alt={`${project.title} screenshot ${idx + 1}`}
                              style={{ maxHeight: '500px', objectFit: 'contain' }}
                            />
                          </Carousel.Item>
                        ))}
                      </Carousel>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default Projects;
